import React, { useEffect, useState } from 'react';
import { Download, FileDown, Search } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/contexts/AuthContext";

interface InvoiceRow {
  stripe_invoice_id: string;
  number: string | null;
  amount_paid: number;
  amount_due: number | null;
  currency: string | null;
  status: string | null;
  description: string | null;
  units: number | null;
  hosted_invoice_url: string | null;
  invoice_pdf: string | null;
  paid_at: string | null;
  created_at: string;
}

function formatMoney(cents: number, currency?: string | null): string {
  const amount = (Number(cents) || 0) / 100;
  const code = (currency || "usd").toUpperCase();
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: code }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${code}`;
  }
}

function formatStatus(status?: string | null): string {
  if (!status) return "Unknown";
  if (status === "paid") return "Paid";
  if (status === "open") return "Open";
  if (status === "void") return "Void";
  if (status === "uncollectible") return "Uncollectible";
  if (status === "draft") return "Draft";
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function safeDownloadUrl(raw?: string | null): string | null {
  if (!raw) return null;
  try {
    const url = new URL(raw);
    if (url.protocol !== "https:") return null;
    return url.toString();
  } catch {
    return null;
  }
}

export function BillingHistory() {
  const { user } = useAuth();
  const [rows, setRows] = useState<InvoiceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  useEffect(() => {
    if (!user) return;
    let active = true;
    (async () => {
      const { data, error } = await supabase
        .from("invoices")
        .select("stripe_invoice_id,number,amount_paid,amount_due,currency,status,description,units,hosted_invoice_url,invoice_pdf,paid_at,created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(200);
      if (!active) return;
      if (error) {
        console.warn("[billing-history]", error.message);
        setLoadError(error.message);
        setRows([]);
      } else {
        setRows((data || []) as InvoiceRow[]);
        setLoadError(null);
      }
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [user]);

  const filteredData = rows.filter((item) => {
    const label = `${item.number || ""} ${item.stripe_invoice_id} ${item.description || ""} ${item.status || ""}`.toLowerCase();
    const matchesSearch = label.includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || (item.status || "") === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.max(1, Math.ceil(filteredData.length / itemsPerPage));

  const handleDownloadInvoice = (item: InvoiceRow) => {
    const url = safeDownloadUrl(item.invoice_pdf) || safeDownloadUrl(item.hosted_invoice_url);
    if (!url) {
      toast.error("No invoice file is available for this payment yet");
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleExportCSV = () => {
    if (!filteredData.length) {
      toast.info("No invoices to export");
      return;
    }
    const header = ["Invoice", "Date", "Description", "Amount", "Currency", "Status", "Units"];
    const lines = filteredData.map((item) => {
      const cents = item.status === "paid" ? item.amount_paid : (item.amount_due ?? item.amount_paid);
      const cells = [
        item.number || item.stripe_invoice_id,
        item.paid_at || item.created_at,
        item.description || "",
        ((Number(cents) || 0) / 100).toFixed(2),
        (item.currency || "").toUpperCase(),
        formatStatus(item.status),
        item.units == null ? "" : String(item.units),
      ];
      return cells.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",");
    });
    const blob = new Blob([[header.join(","), ...lines].join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "og01-billing-history.csv";
    link.click();
    URL.revokeObjectURL(url);
    toast.success("Billing history exported");
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-white/[0.07] bg-og01-card p-6">
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <div className="font-heading text-[20px] text-og01-text">Billing History</div>
          <div className="mt-1 text-[13px] text-og01-text-secondary">View and download your past invoices</div>
        </div>
        <button onClick={handleExportCSV}
          className="inline-flex items-center gap-2 rounded-[10px] border border-white/[0.07] bg-og01-card px-3 py-2 text-[12.5px] font-semibold text-og01-text transition-colors hover:border-white/[0.12]">
          <FileDown className="h-[15px] w-[15px]" />Export CSV
        </button>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex min-w-[200px] flex-1 items-center gap-2.5 rounded-[10px] border border-white/[0.07] bg-[#0F0F18] px-3.5 py-2.5">
          <Search className="h-[15px] w-[15px] text-og01-text-muted" />
          <input placeholder="Search invoices…" value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            className="flex-1 bg-transparent text-[13.5px] text-og01-text outline-none placeholder:text-og01-text-muted" />
        </div>
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
          className="rounded-[9px] border border-white/[0.07] bg-[#0F0F18] px-3 py-2 text-[13px] text-og01-text outline-none">
          <option value="all">All statuses</option>
          <option value="paid">Paid</option>
          <option value="open">Open</option>
          <option value="void">Void</option>
          <option value="uncollectible">Uncollectible</option>
        </select>
        <select value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
          className="rounded-[9px] border border-white/[0.07] bg-[#0F0F18] px-3 py-2 text-[13px] text-og01-text outline-none">
          <option value={5}>5 per page</option>
          <option value={10}>10 per page</option>
          <option value={15}>15 per page</option>
        </select>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-[13.5px]">
          <thead>
            <tr>
              {["Invoice", "Date", "Description", "Amount", "Status"].map((h) => (
                <th key={h} className="px-4 pb-3 text-left text-[11px] font-medium uppercase tracking-[0.05em] text-og01-text-muted">{h}</th>
              ))}
              <th className="px-4 pb-3 text-right text-[11px] font-medium uppercase tracking-[0.05em] text-og01-text-muted">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="border-t border-white/[0.07] py-16 text-center text-og01-text-muted">Loading invoices…</td></tr>
            ) : loadError ? (
              <tr><td colSpan={6} className="border-t border-white/[0.07] py-16 text-center text-og01-text-muted">Invoices could not be loaded. Refresh in a moment.</td></tr>
            ) : currentItems.length > 0 ? currentItems.map((item) => {
              const cents = item.status === "paid" ? item.amount_paid : (item.amount_due ?? item.amount_paid);
              const when = item.paid_at || item.created_at;
              const paid = item.status === "paid";
              return (
                <tr key={item.stripe_invoice_id} className="hover:bg-white/[0.018]">
                  <td className="border-t border-white/[0.07] px-4 py-4 font-medium text-og01-text" style={{ fontFamily: "'Geist Mono', ui-monospace, monospace" }}>{item.number || item.stripe_invoice_id}</td>
                  <td className="border-t border-white/[0.07] px-4 py-4 text-og01-text-secondary">{when ? new Date(when).toLocaleDateString() : "—"}</td>
                  <td className="border-t border-white/[0.07] px-4 py-4 text-og01-text-secondary">{item.description || "Invoice"}</td>
                  <td className="border-t border-white/[0.07] px-4 py-4 text-og01-text-secondary">{formatMoney(cents, item.currency)}</td>
                  <td className="border-t border-white/[0.07] px-4 py-4">
                    <span className="inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold" style={paid ? { color: "#22C55E", background: "color-mix(in srgb, #22C55E 14%, transparent)" } : { color: "#8080A0", background: "rgba(255,255,255,.05)" }}>{formatStatus(item.status)}</span>
                  </td>
                  <td className="border-t border-white/[0.07] px-4 py-4 text-right">
                    <button onClick={() => handleDownloadInvoice(item)}
                      className="inline-grid h-[30px] w-[30px] place-items-center rounded-lg border border-white/[0.07] bg-[#0F0F18] text-og01-text-secondary transition-colors hover:border-white/[0.12] hover:text-og01-text">
                      <Download className="h-[15px] w-[15px]" /><span className="sr-only">Download</span>
                    </button>
                  </td>
                </tr>
              );
            }) : (
              <tr><td colSpan={6} className="border-t border-white/[0.07] py-16 text-center text-og01-text-muted">No invoices yet. Subscription renewals and credit purchases show up here after Stripe finalizes them.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {filteredData.length > 0 && (
        <div className="mt-[18px] flex items-center justify-between text-[13px] text-og01-text-muted">
          <div>
            Showing <b className="font-semibold text-og01-text-secondary">{indexOfFirstItem + 1}</b> to{" "}
            <b className="font-semibold text-og01-text-secondary">{Math.min(indexOfLastItem, filteredData.length)}</b> of{" "}
            <b className="font-semibold text-og01-text-secondary">{filteredData.length}</b> results
          </div>
          <div className="flex gap-2">
            <button onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}
              className="rounded-[10px] border border-white/[0.07] bg-og01-card px-3 py-2 text-[12.5px] font-semibold text-og01-text transition-colors hover:border-white/[0.12] disabled:cursor-not-allowed disabled:opacity-45">
              Previous
            </button>
            <button onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages}
              className="rounded-[10px] border border-white/[0.07] bg-og01-card px-3 py-2 text-[12.5px] font-semibold text-og01-text transition-colors hover:border-white/[0.12] disabled:cursor-not-allowed disabled:opacity-45">
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
